"use client";

import * as React from "react";
import { motion, useReducedMotion } from "motion/react";
import { UploadCloud, Plus } from "lucide-react";
import { cn } from "../../lib/cn";
import { Button } from "../../primitives/Button";
import { springs } from "../../lib/motion";

/**
 * FileDropZone — dashed-border drag-and-drop target with a click-to-browse
 * affordance. Native drag-drop, ZERO dependency.
 *
 * Ported from Creator Studio's `vault/upload/DropZone.tsx` and hardened:
 *   - The donor triggered the hidden `<input>` via
 *     `document.getElementById("file-upload").click()` — fragile (id
 *     collisions when two dropzones mount) and not SSR-safe. We use a `ref`
 *     instead: no `document` access, no id, two zones never collide.
 *   - Inline `blue-50` / `gray-300` washes become brand tokens
 *     (`border-border` / `border-primary`, `bg-primary/5`, `bg-muted/40`).
 *   - Drag highlight is a token color change (kept for reduced-motion users);
 *     the drop-icon's spring scale-up is gated on `useReducedMotion()`.
 *
 * The drag counter (`enter`/`leave` bump a ref) fixes the classic flicker
 * where dragging over a child element fires `dragleave` on the parent. The
 * zone is keyboard-accessible: it is a real `<button>`-less region whose
 * "Browse files" control is a focusable `<Button>`, plus the root responds to
 * Enter / Space to open the picker.
 *
 * @example
 *   <FileDropZone
 *     accept=".pdf,.docx,.txt,.csv,.md"
 *     multiple
 *     onDrop={(files) => addFiles(files)}
 *   />
 *
 *   // Compact variant for "add more" once the queue has entries:
 *   <FileDropZone compact onDrop={addFiles} />
 */
export interface FileDropZoneProps
  extends Omit<
    React.HTMLAttributes<HTMLDivElement>,
    "onDrop" | "title" | "onDrag" | "onDragStart" | "onDragEnd" | "onAnimationStart"
  > {
  /** `accept` filter forwarded to the hidden `<input type="file">`. */
  accept?: string;
  /** Allow selecting / dropping multiple files. Default true. */
  multiple?: boolean;
  /** Disable the whole zone. */
  disabled?: boolean;
  /** Compact "add more files or drag here" variant (smaller padding + icon). */
  compact?: boolean;
  /** Headline text in the full (non-compact) variant. */
  title?: React.ReactNode;
  /** Sub-text under the headline in the full variant. */
  hint?: React.ReactNode;
  /** Fired with the dropped / chosen files. The consumer owns validation. */
  onDrop: (files: File[]) => void;
  /** Override the default empty-state content (full variant only). */
  renderEmpty?: (state: { isDragActive: boolean }) => React.ReactNode;
}

export const FileDropZone = React.forwardRef<HTMLDivElement, FileDropZoneProps>(
  (
    {
      className,
      accept,
      multiple = true,
      disabled = false,
      compact = false,
      title = "Drop files here or click to browse",
      hint = "Supported formats: PDF, DOCX, TXT, and more",
      onDrop,
      renderEmpty,
      ...rest
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    const inputRef = React.useRef<HTMLInputElement>(null);
    const dragDepth = React.useRef(0);
    const [isDragActive, setIsDragActive] = React.useState(false);

    const openPicker = React.useCallback(() => {
      if (!disabled) inputRef.current?.click();
    }, [disabled]);

    const emitFiles = React.useCallback(
      (fileList: FileList | null) => {
        if (!fileList || fileList.length === 0) return;
        onDrop(Array.from(fileList));
      },
      [onDrop],
    );

    const handleDragEnter = (e: React.DragEvent) => {
      e.preventDefault();
      if (disabled) return;
      dragDepth.current += 1;
      setIsDragActive(true);
    };

    const handleDragOver = (e: React.DragEvent) => {
      // Required so the browser treats this as a valid drop target.
      e.preventDefault();
    };

    const handleDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      if (disabled) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setIsDragActive(false);
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      if (disabled) return;
      dragDepth.current = 0;
      setIsDragActive(false);
      emitFiles(e.dataTransfer.files);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openPicker();
      }
    };

    const hiddenInput = (
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        tabIndex={-1}
        onChange={(e) => {
          emitFiles(e.target.files);
          // Reset so re-selecting the same file fires `change` again.
          e.target.value = "";
        }}
      />
    );

    const dropHandlers = {
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
      onKeyDown: handleKeyDown,
    };

    if (compact) {
      return (
        <div
          ref={ref}
          role="button"
          aria-label="Add more files or drag here"
          aria-disabled={disabled || undefined}
          tabIndex={disabled ? -1 : 0}
          onClick={openPicker}
          {...dropHandlers}
          className={cn(
            "rounded-2xl border border-dashed p-4 text-center transition-colors duration-180 ease-smooth-out",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background",
            disabled
              ? "border-border bg-muted/40 cursor-not-allowed opacity-60"
              : isDragActive
                ? "border-primary bg-primary/5 cursor-pointer"
                : "border-border bg-muted/40 hover:bg-muted/60 cursor-pointer",
            className,
          )}
          {...rest}
        >
          {hiddenInput}
          <div className="flex flex-col items-center">
            <motion.span
              animate={isDragActive && !reduced ? { scale: 1.15 } : { scale: 1 }}
              transition={springs.snappy}
              className="mb-2 text-muted-foreground"
            >
              <Plus className="size-6" aria-hidden />
            </motion.span>
            <p className="mb-3 text-sm text-muted-foreground">Add more files or drag here</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                openPicker();
              }}
            >
              Browse files
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        role="button"
        aria-label={typeof title === "string" ? title : "Drop files here or click to browse"}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : 0}
        onClick={openPicker}
        {...dropHandlers}
        className={cn(
          "rounded-2xl border border-dashed p-8 text-center transition-colors duration-180 ease-smooth-out",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background",
          disabled
            ? "border-border bg-muted/40 cursor-not-allowed opacity-60"
            : isDragActive
              ? "border-primary bg-primary/5 cursor-pointer"
              : "border-border bg-background hover:bg-muted/40 cursor-pointer",
          className,
        )}
        {...rest}
      >
        {hiddenInput}
        {renderEmpty ? (
          renderEmpty({ isDragActive })
        ) : (
          <div className="flex flex-col items-center">
            <motion.div
              animate={isDragActive && !reduced ? { scale: 1.12 } : { scale: 1 }}
              transition={springs.snappy}
              className="mb-4 rounded-full bg-primary/10 p-4 text-primary"
            >
              <UploadCloud className="size-10" aria-hidden />
            </motion.div>
            <p className="mb-2 text-xl font-medium text-foreground">{title}</p>
            {hint ? <p className="mb-5 text-sm text-muted-foreground">{hint}</p> : null}
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                openPicker();
              }}
            >
              Browse files
            </Button>
          </div>
        )}
      </div>
    );
  },
);
FileDropZone.displayName = "FileDropZone";
